import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";

type Section = { id: string; title: string; body: string[] };

const SECTIONS: Section[] = [
  {
    id: "overview",
    title: "1. Program overview",
    body: [
      "The Emerging Market Partner Program is how vetted service providers, advocates, and anchor institutions extend our reach so Alabama-grown ideas get the legal, financial, technical, and distribution support they need to ship.",
      "There are three tiers — Advocate, Partner, and Anchor — each with its own commitments and benefits. Most organizations start as a Partner.",
    ],
  },
  {
    id: "conduct",
    title: "2. Code of conduct",
    body: [
      "Center the community. Every engagement is in service of the idea author and their community, not your pipeline.",
      "Honor confidentiality. Ideas are the author's IP. You may not act on, replicate, or share an idea without the author's written acceptance.",
      "Be honest about scope and fit. If you can't help, decline clearly so the platform can route elsewhere.",
      "No predatory pricing or unsolicited upsells on routed leads.",
    ],
  },
  {
    id: "engage-ideas",
    title: "3. Engaging with ideas",
    body: [
      "Express Interest — Browse published ideas in the Ideas Board or your Partner dashboard. When you can offer concrete help (legal, capital, build, distribution, mentorship), click Express Interest, pick the help kind, and send a short message. The idea author is notified and can accept or decline.",
      "Get Matched — Idea authors can also Request a Partner Match. We surface qualified partners by service category and county overlap. Admins may confirm or route the match.",
      "Engagement flow: Pending → Accepted → In-progress → Completed. Authors mark completion; we use these signals for outcomes reporting.",
    ],
  },
  {
    id: "sla",
    title: "4. Response SLAs",
    body: [
      "Partners: Respond to accepted match requests within 3 business days. Respond to author messages within 5 business days.",
      "Anchors: Quarterly stewardship review with our team plus monthly check-ins during active programs.",
      "Repeated SLA misses trigger a check-in, then a tier review.",
    ],
  },
  {
    id: "brand",
    title: "5. Brand & messaging",
    body: [
      "Use 'in partnership with The Emerging Market' or 'a partner of The Emerging Market.' Don't claim endorsement of a specific outcome.",
      "Logos, badges, and approved copy are in your portal under Resources.",
      "When promoting referred content, disclose your role as Partner or Advocate.",
    ],
  },
  {
    id: "reporting",
    title: "6. Reporting & outcomes",
    body: [
      "Quarterly partner report: ideas engaged, scopes proposed, projects shipped, dollars deployed, and lessons learned.",
      "Reports power our public ecosystem dashboards (aggregated, no PII).",
      "Anchors receive bespoke impact dashboards tied to their program commitments.",
    ],
  },
  {
    id: "payouts",
    title: "7. Payouts & revenue share",
    body: [
      "Affiliate referral payouts are display-only today; we'll turn on payout rails once compliance is in place.",
      "Revenue share applies to referred upgrades (e.g., new Pro subscriptions) at standard published rates.",
      "Partner-delivered work is billed by the partner directly to the idea author or sponsor; The Emerging Market does not take a cut on partner-delivered services in this phase.",
    ],
  },
  {
    id: "escalation",
    title: "8. Escalation & disputes",
    body: [
      "First step: contact the idea author directly through the engagement thread.",
      "Unresolved within 5 business days: email program@emrkt.co with the engagement ID.",
      "Tier offboarding is a last resort and follows a 14-day notice and a written review.",
    ],
  },
  {
    id: "lifecycle",
    title: "9. Lifecycle: application → renewal",
    body: [
      "Apply → Admin review (≤ 5 business days) → Onboarding call → Active.",
      "Tier review every 12 months. Promotion or change in tier is based on outcomes, SLA, and community feedback.",
      "Offboarding: notice, final report, deactivation of profile, and a public thank-you when appropriate.",
    ],
  },
  {
    id: "faq",
    title: "10. Quick FAQ",
    body: [
      "Q: Can I be both an Advocate and a Partner? A: Yes — many partners refer too. You'll get one referral code tied to your org.",
      "Q: Can I see who else has expressed interest on an idea? A: Only if the idea author shares that visibility. Default is private.",
      "Q: How do I update my services or counties? A: Portal → Settings → Org profile.",
    ],
  },
];

export default function PartnerHandbook() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/auth?redirect=/partners/handbook");
  }, [loading, user, navigate]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="px-4 sm:px-6 py-12 max-w-4xl mx-auto">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
          <div>
            <Badge variant="outline" className="mb-3">Gated Playbook</Badge>
            <h1 className="font-display text-3xl sm:text-4xl font-bold">Partner Program Handbook</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              The operating playbook for Advocates, Partners, and Anchors. Updated as the program evolves.
            </p>
          </div>
          <Button asChild variant="outline" size="sm">
            <a href="/handbook/emrkt-partner-overview.pdf" target="_blank" rel="noreferrer">
              Download one-pager (PDF)
            </a>
          </Button>
        </div>

        {/* TOC */}
        <Card className="p-4 mb-8">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Contents</div>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-foreground hover:text-primary">
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </Card>

        <div className="space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24">
              <h2 className="font-display text-2xl font-semibold mb-3">{s.title}</h2>
              <div className="space-y-3 text-foreground/90 leading-relaxed">
                {s.body.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 flex gap-3">
          <Button asChild>
            <Link to="/partners/apply">Apply to the program</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/partner">Go to Partner Hub</Link>
          </Button>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}